import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "hooks/useAuth";
import useChartMindSession from "hooks/useChartMindSession";
import { setLLMEncounterId } from "services/llmService";

/**
 * ChartMindSession context — owns the live ChartMind session above the route boundary so
 * the encounter survives navigation (e.g. clicking ChatPage in the sidebar and coming back).
 *
 * HIPAA note: state is RAM-only — never written to localStorage / sessionStorage / cookies.
 * It dies on tab refresh and on auth tear-down (the provider mounts inside the authenticated
 * dashboard tree). That's the feature, not a bug — consumers must handle "no encounter"
 * gracefully.
 *
 * For Kijabe beta users (userData.betaAccess) the provider also passes disableSave=true so
 * Firestore persistence stays nooped.
 */

const ChartMindSessionContext = createContext(null);

export const ChartMindSessionProvider = ({ children }) => {
  const { userData, isGlobalAdmin, organizationId } = useAuth();

  // [BETA-KIJABE] Mirror the same flag ProfessionalRoutes uses — remove when beta ends.
  const isBetaKijabe = Boolean(userData?.betaAccess);

  // Admins use ChartMind only to simulate encounters for prompt development, so their
  // sessions are ephemeral too — keep Firestore persistence nooped. Mirrors isSimulationMode
  // in ChartMindPage.
  const isSimulationMode =
    isGlobalAdmin || (userData?.role === "admin" && Boolean(organizationId));

  // Always start with no initial session ID. Non-ephemeral URL → sessionId reconciliation is
  // handled inside ChartMindPage by calling session.session.loadSession(urlSessionId).
  const session = useChartMindSession(null, {
    disableSave: isBetaKijabe || isSimulationMode,
  });

  // Tag every LLM call with the encounter it belongs to, so cost per encounter is a real
  // per-encounter figure rather than an average over completed sessions. Set here rather
  // than at each call site — one place to get right, and it follows the session's lifetime.
  //
  // Ephemeral encounters (Kijabe beta, admin simulation) get a generated id rather than
  // being left null. They never write a session doc, so before this they were invisible to
  // any per-encounter denominator while still costing money — in prod that made ChartMind
  // look like ~$0.22 per encounter when the real figure was ~$0.03.
  const ephemeralEncounterIdRef = useRef(null);
  const sessionId = session.session.sessionId;

  useEffect(() => {
    if (sessionId) {
      setLLMEncounterId(sessionId, { simulation: isSimulationMode });
      return;
    }
    // sessionId goes null on mount and on "New encounter", so a fresh id per encounter.
    ephemeralEncounterIdRef.current = `eph-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    setLLMEncounterId(ephemeralEncounterIdRef.current, { simulation: isSimulationMode });
  }, [sessionId, isSimulationMode]);

  // PeerView chat messages live here (not inside PeerViewPage) so they survive
  // navigation away and back. They're cleared only when a new encounter starts.
  const [peerViewMessages, setPeerViewMessages] = useState([]);
  const clearPeerViewMessages = useCallback(() => setPeerViewMessages([]), []);

  const value = useMemo(
    () => ({ ...session, peerViewMessages, setPeerViewMessages, clearPeerViewMessages }),
    [session, peerViewMessages, setPeerViewMessages, clearPeerViewMessages],
  );

  return (
    <ChartMindSessionContext.Provider value={value}>
      {children}
    </ChartMindSessionContext.Provider>
  );
};

/**
 * Read the live ChartMind session.
 *
 * Throws if used outside the provider — that's intentional. ChatPage and ChartMindPage
 * are both inside ProfessionalRoutes' provider tree; if you hit this error, something
 * else is wrong (e.g. consumed from the patient/onboarding routes).
 */
export const useChartMindContext = () => {
  const ctx = useContext(ChartMindSessionContext);
  if (!ctx) {
    throw new Error(
      "useChartMindContext must be used inside <ChartMindSessionProvider>",
    );
  }
  return ctx;
};

export default ChartMindSessionContext;
